import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import SearchIcon from '@mui/icons-material/Search'
import {
  browseHighlights,
  listHighlightTags,
  listUploads
} from '../service'
import HighlightPreviewCard from '../components/card/HighlightPreviewCard'
import { useAuth } from '../auth/AuthContext'
import useRecentSearches, { hasSavedFilterValues } from '../hooks/useRecentSearches'

const PAGE_SIZES = [24, 48, 96]

const emptyDraft = {
  q: '',
  tags: [],
  uploadId: '',
  uploadQuery: '',
  owner: '',
  from: '',
  to: ''
}

const toStartOfDayIso = value => {
  if (!value) return undefined
  return new Date(`${value}T00:00:00.000`).toISOString()
}

const toEndOfDayIso = value => {
  if (!value) return undefined
  return new Date(`${value}T23:59:59.999`).toISOString()
}

const filtersFromSearchParams = searchParams => ({
  q: searchParams.get('q') || '',
  tags: searchParams.getAll('tag'),
  uploadId: searchParams.get('upload') || '',
  uploadQuery: searchParams.get('source') || '',
  owner: searchParams.get('owner') || '',
  from: searchParams.get('from') || '',
  to: searchParams.get('to') || ''
})

const searchParamsFromFilters = (filters, currentParams = null) => {
  const params = new URLSearchParams()
  if (filters.q) params.set('q', filters.q)
  for (const tag of filters.tags || []) params.append('tag', tag)
  if (filters.uploadId) params.set('upload', filters.uploadId)
  if (!filters.uploadId && filters.uploadQuery) params.set('source', filters.uploadQuery)
  if (filters.owner) params.set('owner', filters.owner)
  if (filters.from) params.set('from', filters.from)
  if (filters.to) params.set('to', filters.to)
  if (currentParams && currentParams.get('__e2e_authenticated') === '1') {
    params.set('__e2e_authenticated', '1')
  }
  return params
}

const describeHighlightSearch = (filters, uploadName = '') => {
  const parts = []
  if (filters.q) parts.push(`Search: ${filters.q}`)
  if ((filters.tags || []).length) parts.push(`Tags: ${filters.tags.join(', ')}`)
  if (uploadName) parts.push(`Source: ${uploadName}`)
  else if (filters.uploadQuery) parts.push(`Source: ${filters.uploadQuery}`)
  else if (filters.uploadId) parts.push('Specific source')
  if (filters.owner) parts.push(`Owner: ${filters.owner}`)
  if (filters.from || filters.to) {
    parts.push(`Date: ${filters.from || 'any'} to ${filters.to || 'any'}`)
  }
  return parts.join(' · ') || 'All highlights'
}

const hasHighlightFilterParams = searchParams =>
  ['q', 'tag', 'upload', 'source', 'owner', 'from', 'to']
    .some(key => searchParams.has(key))

const Highlight = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const {
    lastSearch,
    recentSearches,
    rememberSearch,
    clearLastSearch,
    clearRecentSearches
  } = useRecentSearches({
    userKey: user?.username,
    scope: 'highlights'
  })
  const searchKey = searchParams.toString()
  const appliedFilters = useMemo(
    () => filtersFromSearchParams(searchParams),
    [searchKey]
  )

  const [draft, setDraft] = useState(appliedFilters)
  const [highlights, setHighlights] = useState([])
  const [tags, setTags] = useState([])
  const [uploads, setUploads] = useState([])
  const [nextCursor, setNextCursor] = useState(null)
  const [total, setTotal] = useState(0)
  const [pageSize, setPageSize] = useState(24)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [showMobileFilters, setShowMobileFilters] = useState(false)

  useEffect(() => {
    if (!lastSearch || hasHighlightFilterParams(searchParams)) return
    setSearchParams(searchParamsFromFilters(lastSearch, searchParams), { replace: true })
  }, [lastSearch, searchKey, searchParams, setSearchParams])

  useEffect(() => {
    setDraft(appliedFilters)
  }, [searchKey])

  useEffect(() => {
    let active = true

    const loadFacets = async () => {
      try {
        const [tagRows, uploadRows] = await Promise.all([
          listHighlightTags(100),
          listUploads()
        ])
        if (!active) return
        setTags(tagRows)
        setUploads(uploadRows)
      } catch (err) {
        console.error(err)
      }
    }

    loadFacets()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    let active = true

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const result = await browseHighlights({
          q: appliedFilters.q || undefined,
          tags: appliedFilters.tags.length ? appliedFilters.tags : undefined,
          uploadId: appliedFilters.uploadId || undefined,
          uploadQuery: appliedFilters.uploadQuery || undefined,
          owner: appliedFilters.owner || undefined,
          fromDate: toStartOfDayIso(appliedFilters.from),
          toDate: toEndOfDayIso(appliedFilters.to),
          limit: pageSize
        })

        if (!active) return
        setHighlights(result.items)
        setNextCursor(result.nextCursor)
        setTotal(result.total)
      } catch (err) {
        console.error(err)
        if (active) {
          setError(err)
          setHighlights([])
          setNextCursor(null)
          setTotal(0)
        }
      } finally {
        if (active) setLoading(false)
      }
    }

    load()
    return () => {
      active = false
    }
  }, [searchKey, pageSize])

  const applyFilters = () => {
    setSearchParams(searchParamsFromFilters(draft, searchParams))
    if (hasSavedFilterValues(draft)) {
      rememberSearch(
        draft,
        describeHighlightSearch(draft, selectedUpload?.name || '')
      )
    } else {
      clearLastSearch()
    }
  }

  const applyRecentSearch = entry => {
    setDraft(entry.filters)
    setSearchParams(searchParamsFromFilters(entry.filters, searchParams))
    rememberSearch(entry.filters, entry.label)
  }

  const clearFilters = () => {
    setDraft(emptyDraft)
    const params = new URLSearchParams()
    if (searchParams.get('__e2e_authenticated') === '1') {
      params.set('__e2e_authenticated', '1')
    }
    setSearchParams(params)
    clearLastSearch()
  }

  const selectTag = tag => {
    const tags = appliedFilters.tags.includes(tag)
      ? appliedFilters.tags.filter(value => value !== tag)
      : [...appliedFilters.tags, tag]
    const next = {
      ...appliedFilters,
      tags
    }
    setDraft(next)
    setSearchParams(searchParamsFromFilters(next, searchParams))
    if (hasSavedFilterValues(next)) {
      rememberSearch(next, describeHighlightSearch(next))
    } else {
      clearLastSearch()
    }
  }

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return

    setLoadingMore(true)
    setError(null)
    try {
      const result = await browseHighlights({
        q: appliedFilters.q || undefined,
        tags: appliedFilters.tags.length ? appliedFilters.tags : undefined,
        uploadId: appliedFilters.uploadId || undefined,
        uploadQuery: appliedFilters.uploadQuery || undefined,
        owner: appliedFilters.owner || undefined,
        fromDate: toStartOfDayIso(appliedFilters.from),
        toDate: toEndOfDayIso(appliedFilters.to),
        cursor: nextCursor,
        limit: pageSize
      })
      setHighlights(current => current.concat(result.items))
      setNextCursor(result.nextCursor)
      setTotal(result.total)
    } catch (err) {
      console.error(err)
      setError(err)
    } finally {
      setLoadingMore(false)
    }
  }

  const selectedUpload = uploads.find(upload => upload.id === draft.uploadId) || null

  useEffect(() => {
    if (
      draft.uploadId ||
      (
        draft.uploadQuery === appliedFilters.uploadQuery &&
        !appliedFilters.uploadId
      )
    ) return

    const timer = setTimeout(() => {
      const next = {
        ...appliedFilters,
        uploadId: '',
        uploadQuery: draft.uploadQuery
      }
      setSearchParams(searchParamsFromFilters(next, searchParams))
      if (hasSavedFilterValues(next)) {
        rememberSearch(
          next,
          describeHighlightSearch(next),
          { addToRecent: false }
        )
      } else {
        clearLastSearch()
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [draft.uploadId, draft.uploadQuery, appliedFilters.uploadQuery, searchKey])
  const selectedTags = tags.filter(tag => draft.tags.includes(tag.name))
  const activeFilterCount = [
    appliedFilters.q,
    appliedFilters.tags.length > 0,
    appliedFilters.uploadId || appliedFilters.uploadQuery,
    appliedFilters.owner,
    appliedFilters.from,
    appliedFilters.to
  ].filter(Boolean).length
  const hasDraftFilters = [
    draft.q,
    draft.tags.length > 0,
    draft.uploadId || draft.uploadQuery,
    draft.owner,
    draft.from,
    draft.to
  ].some(Boolean)

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1700, mx: 'auto', px: { xs: 0, sm: 1 } }}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Highlights
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Browse your full accessible highlight library. Search, filter, and load more without the old six-month limit.
        </Typography>
      </Box>

      <Button
        variant="outlined"
        startIcon={<FilterAltIcon />}
        onClick={() => setShowMobileFilters(value => !value)}
        sx={{ display: { xs: 'inline-flex', sm: 'none' }, alignSelf: 'flex-start' }}
      >
        {showMobileFilters ? 'Hide filters' : `Filters${activeFilterCount ? ` (${activeFilterCount})` : ''}`}
      </Button>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.5, md: 2 },
          display: { xs: showMobileFilters ? 'block' : 'none', sm: 'block' }
        }}
      >
        <Stack spacing={2}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={5}>
              <TextField
                label="Search highlights"
                placeholder="Highlight name, description, or upload"
                value={draft.q}
                onChange={event => setDraft(current => ({
                  ...current,
                  q: event.target.value
                }))}
                onKeyDown={event => {
                  if (event.key === 'Enter') applyFilters()
                }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={tags}
                getOptionLabel={option => option.name}
                isOptionEqualToValue={(option, value) => option.name === value.name}
                value={selectedTags}
                onChange={(_event, value) => setDraft(current => ({
                  ...current,
                  tags: value.map(tag => tag.name)
                }))}
                renderInput={params => (
                  <TextField {...params} label="Tags" placeholder={draft.tags.length ? '' : 'Any tags'} />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={uploads}
                getOptionLabel={option => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedUpload}
                inputValue={draft.uploadId && selectedUpload
                  ? selectedUpload.name
                  : draft.uploadQuery}
                onInputChange={(_event, value, reason) => {
                  if (reason !== 'input' && reason !== 'clear') return
                  setDraft(current => ({
                    ...current,
                    uploadId: '',
                    uploadQuery: value
                  }))
                }}
                onChange={(_event, value) => {
                  const next = {
                    ...appliedFilters,
                    uploadId: value ? value.id : '',
                    uploadQuery: ''
                  }
                  setDraft(current => ({
                    ...current,
                    uploadId: next.uploadId,
                    uploadQuery: value ? value.name : ''
                  }))
                  setSearchParams(searchParamsFromFilters(next, searchParams))
                  if (hasSavedFilterValues(next)) {
                    rememberSearch(
                      next,
                      describeHighlightSearch(next, value?.name || '')
                    )
                  } else {
                    clearLastSearch()
                  }
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Upload / game"
                    placeholder="Type to filter by source"
                    helperText="Type part of a source name, or select an exact upload"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Owner"
                placeholder="Username"
                value={draft.owner}
                onChange={event => setDraft(current => ({
                  ...current,
                  owner: event.target.value
                }))}
                fullWidth
              />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <TextField
                label="From"
                type="date"
                value={draft.from}
                onChange={event => setDraft(current => ({
                  ...current,
                  from: event.target.value
                }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <TextField
                label="To"
                type="date"
                value={draft.to}
                onChange={event => setDraft(current => ({
                  ...current,
                  to: event.target.value
                }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                label="Page size"
                value={pageSize}
                onChange={event => setPageSize(Number(event.target.value))}
                fullWidth
              >
                {PAGE_SIZES.map(size => (
                  <MenuItem key={size} value={size}>{size}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6} sm={9} md={3}>
              <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={applyFilters}
                  fullWidth
                >
                  Apply
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<FilterAltOffIcon />}
                  onClick={clearFilters}
                  disabled={activeFilterCount === 0 && !hasDraftFilters}
                  fullWidth
                >
                  Clear
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {recentSearches.length > 0
            ? (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Recent searches
                </Typography>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
                  {recentSearches.map(entry => (
                    <Chip
                      key={entry.signature || entry.savedAt}
                      label={entry.label}
                      size="small"
                      variant="outlined"
                      onClick={() => applyRecentSearch(entry)}
                    />
                  ))}
                  <Button size="small" color="inherit" onClick={clearRecentSearches}>
                    Clear recent
                  </Button>
                </Stack>
              </Box>
              )
            : null}

          {tags.length > 0
            ? (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Popular tags
                </Typography>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  {tags.slice(0, 16).map(tag => (
                    <Chip
                      key={tag.name}
                      label={`${tag.name} (${tag.count})`}
                      size="small"
                      variant={appliedFilters.tags.includes(tag.name) ? 'filled' : 'outlined'}
                      color={appliedFilters.tags.includes(tag.name) ? 'primary' : 'default'}
                      onClick={() => selectTag(tag.name)}
                    />
                  ))}
                </Stack>
              </Box>
              )
            : null}
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
        <Typography variant="body2" color="text.secondary">
          {loading
            ? 'Loading highlights...'
            : `Showing ${highlights.length.toLocaleString()} of ${total.toLocaleString()} matching highlights`}
        </Typography>
        {activeFilterCount > 0
          ? (
            <Typography variant="body2" color="text.secondary">
              {activeFilterCount} active {activeFilterCount === 1 ? 'filter' : 'filters'}
            </Typography>
            )
          : null}
      </Stack>

      {error
        ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography color="error">
              Error while retrieving highlights: {error.message || String(error)}
            </Typography>
          </Paper>
          )
        : null}

      {loading
        ? (
          <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}>
            <CircularProgress />
          </Box>
          )
        : null}

      {!loading && highlights.length === 0 && !error
        ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6">No highlights matched these filters.</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Clear a filter or broaden the date range to search the rest of your library.
            </Typography>
          </Paper>
          )
        : null}

      {!loading && highlights.length > 0
        ? (
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {highlights.map(highlight => (
              <Grid item key={highlight.id} xs={12} sm={6} lg={4} xl={3}>
                <HighlightPreviewCard highlight={highlight} />
              </Grid>
            ))}
          </Grid>
          )
        : null}

      {nextCursor
        ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 2 }}>
            <Button
              variant="outlined"
              onClick={loadMore}
              disabled={loadingMore}
              sx={{ minWidth: 220 }}
            >
              {loadingMore ? 'Loading...' : 'Load more highlights'}
            </Button>
          </Box>
          )
        : null}
    </Stack>
  )
}

export default Highlight
